import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import LessonExternalLinks from '../../student-lesson-page/components/LessonExternalLinks';

const RelatedVideos = ({ videos = [], currentVideoId, onVideoSelect, externalLinks = [] }) => {
  const navigate = useNavigate();

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds?.toString()?.padStart(2, '0')}`;
  };

  const handleVideoClick = (video) => {
    if (onVideoSelect) {
      onVideoSelect(video);
    } else {
      navigate(`/video-player-interface?id=${video?.id}`);
    }
  };

  const filteredVideos = videos?.filter(video => video?.id !== currentVideoId);

  if (filteredVideos?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <div className="p-4 border-b border-border">
          <h3 className="font-heading font-medium text-foreground">
            Vídeos Relacionados
          </h3>
        </div>
        <div className="p-4">
          <LessonExternalLinks externalLinks={externalLinks} />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center space-x-3">
          <Icon name="PlayCircle" size={20} className="text-foreground" />
          <h3 className="font-heading font-medium text-foreground">
            Próximas lições ({filteredVideos?.length})
          </h3>
        </div>
      </div>
      {/* Video List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredVideos?.map((video, index) => (
          <div
            key={video?.id}
            className="flex items-start space-x-4 p-4 border-b border-border last:border-b-0 hover:bg-accent hover:text-accent-foreground transition-micro cursor-pointer group"
            onClick={() => handleVideoClick(video)}
          >
            {/* Video Number */}
            <div className="w-8 h-8 rounded-full bg-muted text-muted-foreground flex items-center justify-center text-sm font-medium flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-micro">
              {index + 1}
            </div>

            {/* Thumbnail */}
            <div className="relative w-24 h-16 rounded-lg overflow-hidden flex-shrink-0 bg-muted">
              <Image
                src={video?.thumbnail}
                alt={video?.title}
                className="w-full h-full object-cover"
              />
              
              {/* Duration Badge */}
              <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded font-caption">
                {formatDuration(video?.duration)}
              </div>

              {/* Play Overlay */}
              <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <Icon name="Play" size={16} className="text-white" />
              </div>
            </div>

            {/* Video Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-foreground group-hover:text-accent-foreground line-clamp-2 mb-1">
                {video?.title}
              </h4>
              
              <div className="flex items-center space-x-3 text-sm text-muted-foreground font-caption mb-2">
                <span>Módulo {video?.moduleNumber}</span>
                <span>•</span>
                <span>{formatDuration(video?.duration)}</span>
              </div>

              {/* Progress Bar */}
              {video?.progress > 0 && (
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-muted-foreground font-caption">
                      {video?.progress === 100 ? 'Concluído' : 'Em progresso'}
                    </span>
                    <span className="text-muted-foreground font-caption">
                      {Math.round(video?.progress)}%
                    </span>
                  </div>
                  <div className="w-full h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-300 ${
                        video?.progress === 100 ? 'bg-success' : 'bg-primary'
                      }`}
                      style={{ width: `${video?.progress}%` }}
                    />
                  </div>
                </div>
              )}

              {/* Status Badge */}
              <div className="flex items-center space-x-2 mt-2">
                {video?.isNew && (
                  <span className="px-2 py-0.5 bg-secondary text-secondary-foreground text-xs rounded-full font-caption font-medium">
                    Novo
                  </span>
                )}
                {video?.progress === 100 && (
                  <div className="flex items-center space-x-1">
                    <Icon name="CheckCircle" size={12} className="text-success" />
                    <span className="text-xs text-success font-caption">Concluído</span>
                  </div>
                )}
                {video?.isLocked && (
                  <div className="flex items-center space-x-1">
                    <Icon name="Lock" size={12} className="text-muted-foreground" />
                    <span className="text-xs text-muted-foreground font-caption">Bloqueado</span>
                  </div>
                )}
              </div>
            </div>

            {/* Action Button */}
            <div className="flex-shrink-0">
              <Button
                variant="ghost"
                size="sm"
                iconName="Play"
                iconSize={16}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
                onClick={(e) => {
                  e?.stopPropagation();
                  handleVideoClick(video);
                }}
              />
            </div>
          </div>
        ))}
      </div>
      {/* View All Button */}
      <div className="p-4 border-t border-border">
        <Button
          variant="outline"
          fullWidth
          iconName="ArrowRight"
          iconPosition="right"
          onClick={() => navigate('/course-detail-view')}
        >
          Ver todas as lições
        </Button>
      </div>
    </div>
  );
};

export default RelatedVideos;